import supabase from '../supabase/supabase';

const requireOrganizationId = (organizationId) => {
  if (!organizationId) {
    throw new Error('Organization ID is required.');
  }
};

const cleanText = (value) =>
  String(value ?? '').trim();

const toMinorUnits = (value) => {
  const text = String(value ?? '').trim();

  if (!/^\d+(\.\d{1,2})?$/.test(text)) {
    throw new Error(
      'Enter a valid amount with no more than two decimal places.'
    );
  }

  const [whole, fraction = ''] = text.split('.');
  const cents =
    Number(whole) * 100 +
    Number(fraction.padEnd(2, '0'));

  if (!Number.isSafeInteger(cents) || cents <= 0) {
    throw new Error('Enter an amount greater than zero.');
  }

  return cents;
};

const fromMinorUnits = (value) =>
  Number(value || 0) / 100;

const checkError = (error, fallback) => {
  if (error) {
    console.error('[Payments Received]', error);
    throw new Error(error.message || fallback);
  }
};

class PaymentService {
  toMinorUnits(value) {
    return toMinorUnits(value);
  }

  fromMinorUnits(value) {
    return fromMinorUnits(value);
  }

  async getPayments(organizationId) {
    requireOrganizationId(organizationId);

    const { data: payments, error } = await supabase
      .schema('accounting')
      .from('customer_payments')
      .select('*')
      .eq('organization_id', organizationId)
      .order('payment_date', { ascending: false })
      .order('created_at', { ascending: false });

    checkError(error, 'Unable to load payments.');

    if (!payments?.length) return [];

    const customerIds = [
      ...new Set(payments.map((p) => p.customer_id))
    ];

    const paymentIds = payments.map((p) => p.id);

    const [customersResult, allocationsResult] =
      await Promise.all([
        supabase
          .schema('accounting')
          .from('customers')
          .select('id, name, email, company_name')
          .eq('organization_id', organizationId)
          .in('id', customerIds),

        supabase
          .schema('accounting')
          .from('customer_payment_allocations')
          .select('id, payment_id, invoice_id, amount')
          .eq('organization_id', organizationId)
          .in('payment_id', paymentIds)
      ]);

    checkError(
      customersResult.error,
      'Unable to load payment customers.'
    );

    checkError(
      allocationsResult.error,
      'Unable to load payment allocations.'
    );

    const allocations = allocationsResult.data || [];
    const invoiceIds = [
      ...new Set(allocations.map((a) => a.invoice_id))
    ];

    let invoices = [];

    if (invoiceIds.length) {
      const result = await supabase
        .schema('accounting')
        .from('invoices')
        .select(
          'id, invoice_number, status, currency, total_amount, amount_due'
        )
        .eq('organization_id', organizationId)
        .in('id', invoiceIds);

      checkError(
        result.error,
        'Unable to load related invoices.'
      );

      invoices = result.data || [];
    }

    const customerMap = new Map(
      (customersResult.data || []).map((c) => [c.id, c])
    );

    const invoiceMap = new Map(
      invoices.map((i) => [i.id, i])
    );

    const allocationMap = new Map();

    for (const allocation of allocations) {
      const list = allocationMap.get(allocation.payment_id) || [];

      list.push({
        ...allocation,
        invoice: invoiceMap.get(allocation.invoice_id) || null
      });

      allocationMap.set(allocation.payment_id, list);
    }

    return payments.map((payment) => ({
      ...payment,
      customer: customerMap.get(payment.customer_id) || null,
      allocations: allocationMap.get(payment.id) || []
    }));
  }

  async getEligibleInvoices(organizationId) {
    requireOrganizationId(organizationId);

    const { data, error } = await supabase
      .schema('accounting')
      .from('invoices')
      .select(`
        id,
        organization_id,
        customer_id,
        invoice_number,
        status,
        currency,
        total_amount,
        amount_paid,
        amount_due,
        stripe_invoice_id,
        due_date
      `)
      .eq('organization_id', organizationId)
      .is('stripe_invoice_id', null)
      .gt('amount_due', 0)
      .in('status', [
        'open',
        'sent',
        'unpaid',
        'overdue',
        'partially_paid'
      ])
      .order('created_at', { ascending: false });

    checkError(error, 'Unable to load unpaid invoices.');

    return data || [];
  }

  async getCustomers(organizationId) {
    requireOrganizationId(organizationId);

    const { data, error } = await supabase
      .schema('accounting')
      .from('customers')
      .select('id, name, email, company_name, currency')
      .eq('organization_id', organizationId)
      .order('name', { ascending: true });

    checkError(error, 'Unable to load customers.');

    return data || [];
  }

  async recordManualPayment(organizationId, payload) {
    requireOrganizationId(organizationId);

    if (!payload?.invoiceId) {
      throw new Error('Select an invoice.');
    }

    const amount = toMinorUnits(payload.amount);

    const allowedMethods = [
      'bank_transfer',
      'cash',
      'check',
      'other'
    ];

    if (!allowedMethods.includes(payload.paymentMethod)) {
      throw new Error('Select a valid payment method.');
    }

    const { data, error } = await supabase
      .schema('accounting')
      .rpc('record_manual_invoice_payment', {
        p_organization_id: organizationId,
        p_invoice_id: payload.invoiceId,
        p_amount: amount,
        p_payment_date: payload.paymentDate || null,
        p_payment_method: payload.paymentMethod,
        p_reference_number:
          cleanText(payload.referenceNumber) || null,
        p_notes: cleanText(payload.notes)
      });

    checkError(error, 'Unable to record payment.');

    if (!data) {
      throw new Error('Payment was not confirmed by the database.');
    }

    return data;
  }
}

export const paymentService = new PaymentService();